import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * The snackbar: one message, top centre of the screen, on a solid colour —
 * green for saved, red for failed, yellow for "not allowed / check this". What
 * each colour means is defined in utils/notify.js; most callers should go
 * through `notify` and the `SnackbarHost` rather than render this directly.
 *
 * Errors and warnings render with `role="alert"` so they are announced at once
 * (and found by the e2e suite's `getByRole("alert")`); a success is
 * `role="status"`, announced politely.
 *
 * It closes itself after `duration`, except while the pointer or keyboard
 * focus is on it — a long API message must not vanish mid-read.
 *
 * Replaces MUI's Snackbar/Alert, which was the only thing pulling @mui/material
 * into the bundle and shipped its own palette that clashed with the brand green.
 */
const SEVERITY = {
  // brand-700, not 600: white on brand-600 is under 4.5:1.
  success: {
    wrap: "bg-brand-700 text-white",
    icon: "bi-check-circle-fill",
    role: "status",
  },
  error: {
    wrap: "bg-red-600 text-white",
    icon: "bi-x-circle-fill",
    role: "alert",
  },
  // Dark text: white on yellow is unreadable.
  warning: {
    wrap: "bg-yellow-400 text-ink-900",
    icon: "bi-exclamation-triangle-fill",
    role: "alert",
  },
};

const Toast = ({
  open,
  message,
  onClose,
  severity = "error",
  duration = 6000,
}) => {
  const { t } = useTranslation();
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!open || !duration || paused) return undefined;
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [open, duration, paused, onClose]);

  if (!open) return null;

  const styles = SEVERITY[severity] ?? SEVERITY.error;

  return (
    // A strip across the top of the viewport centres the card horizontally and
    // lets every click through around it; only the card itself takes the
    // pointer. z-1400 sits above the modals (1100) and anything stacked over
    // them (up to 1300), because that is where most saves happen.
    <div className="pointer-events-none fixed inset-x-0 top-0 z-1400 flex justify-center p-4 sm:pt-6">
      <div
        role={styles.role}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className={`snackbar pointer-events-auto flex w-full max-w-md animate-snackbar-in items-center gap-3 rounded-xl py-2 pr-2 pl-4 shadow-card-hover motion-reduce:animate-none ${styles.wrap}`}
      >
        <i className={`bi ${styles.icon} shrink-0 text-xl`} aria-hidden="true" />
        <span className="min-w-0 flex-1 py-1.5 text-fluid-sm font-semibold break-words">
          {message}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("Common.dismiss")}
          className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg opacity-80 transition hover:bg-black/10 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

Toast.propTypes = {
  open: PropTypes.bool,
  message: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  severity: PropTypes.oneOf(["error", "success", "warning"]),
  duration: PropTypes.number,
};

export default Toast;
